import { useContext, useMemo } from 'react'

import {
  DataWarehouseAvailabilityResponse,
  DataWarehouseProvider,
  DataWarehouseQueryResponse,
  DataWarehouseReportGrouping,
  useDataWarehouseQuery,
  useOrgUnitHierarchy,
} from 'components/DataWarehouse'

import { flatten, some, sortBy, uniq } from 'lodash-es'

import { getPreviousDateRange, PerformanceTimeseriesPreset, timeseriesPeriodCalculators } from './lib'

import { OrgUnitDataWarehouseAvailabilityContext } from 'contexts'

import { DashboardReferenceDatesContext } from '../ReferenceDatesProvider'

import { getWarehouseAvailabilityForReportType } from '../lib'

import { reportProvidersV2 } from '@percept/constants'

import { PlatformUnit, ReportProviderV2 } from '@percept/types'


type PerformanceReport = 'CHANNEL' | 'MARKETING_FUNNEL' | 'CAMPAIGN_OBJECTIVE' | 'UNSEGMENTED'

type PerformanceReportConfig = Record<PerformanceReport, ReportProviderV2[]>

const performanceReportConfig: PerformanceReportConfig = {
  CAMPAIGN_OBJECTIVE: ['FACEBOOK', 'TIKTOK'],
  CHANNEL: ['GOOGLE_ADS'],
  MARKETING_FUNNEL: ['FACEBOOK', 'TIKTOK'],
  UNSEGMENTED: ['DV360'],
}


type PerformanceReportAvailability = {
  minDate: Date | null
  maxDate: Date | null
  providers: DataWarehouseProvider[]
}

const getPerformanceReportAvailabilityByType = ({
  orgUnitId,
  orgUnitHierarchy,
  availabilityResponse,
  groupings,
}: {
  orgUnitId: string
  orgUnitHierarchy: PlatformUnit | null
  availabilityResponse: DataWarehouseAvailabilityResponse[] | undefined | null
  groupings: DataWarehouseReportGrouping[] | undefined | null
}): Record<PerformanceReport, PerformanceReportAvailability> => {
  return Object.entries(performanceReportConfig).reduce( (acc, [reportType, filteredProviders]) => {
    acc[reportType as PerformanceReport] = getWarehouseAvailabilityForReportType({
      reportType,
      orgUnitId,
      reportGroupings: groupings,
      availability: availabilityResponse,
      orgUnitHierarchy,
      providerFilter: filteredProviders,
    })
    return acc
  }, {} as Record<PerformanceReport, PerformanceReportAvailability>)
}


type QueryResultLike<T = unknown> = {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  isRefetching: boolean
}

type DerivedPerformanceRow = {
  provider: ReportProviderV2
  segment: string | null
  metric: string
  value: number | null
  org_unit_id: string
  org_unit_name: string
  period_start: Date
  period_end: Date
}

export const unsegmentedMetrics = {
  'UNSEGMENTED': {
    key: 'average_cpm',
    label: 'CPM',
  }
}

export const funnelMetrics = {
  'Awareness': {
    key: 'average_cpm',
    label: 'CPM'
  },
  'Consideration': {
    key: 'average_cpc',
    label: 'CPC',
  },
  'Conversion': {
    key: 'cost_per_conversion',
    label: 'CPA',
  }
}

export const channelMetrics = {
  'Search': {
    key: 'cost_per_conversion',
    label: 'CPA',
  },
  'Performance Max': {
    key: 'cost_per_conversion',
    label: 'CPA',
  },
  'Video': {
    key: 'average_cpv',
    label: 'CPV',
  },
}

export const videoViewMetrics = {
  'Video Views': {
    key: 'average_cpv',
    label: 'CPV',
  },
}

export const allSegmentMetrics = {
  ...unsegmentedMetrics,
  ...funnelMetrics,
  ...channelMetrics,
  ...videoViewMetrics,
}


const deriveCombinedPerformanceRows = ({
  data,
  dateRange,
  previousData,
  previousDateRange,
  includesPreviousPeriod,
}: {
  data: DataWarehouseQueryResponse | undefined
  previousData: DataWarehouseQueryResponse | undefined
  dateRange: Record<'start' | 'end', Date | null>
  previousDateRange: Record<'start' | 'end', Date | null>
  isAggregatedToSingleOrgUnit?: boolean
  includesPreviousPeriod?: boolean
}): DerivedPerformanceRow[] | undefined => {

  if( !data || (includesPreviousPeriod && !previousData) ){
    return undefined
  }

  const performanceRows: DerivedPerformanceRow[] = []

  const dateRangeDataPairs: [DataWarehouseQueryResponse, Record<'start' | 'end', Date | null>][] = [
    [data, dateRange],
  ]
  if( includesPreviousPeriod ){
    dateRangeDataPairs.push([previousData as DataWarehouseQueryResponse, previousDateRange])
  }

  for( const [data, dateRange] of dateRangeDataPairs ){
    if( dateRange.start && dateRange.end ){
      for( const row of data.rows ){
        const { grouping, segment, provider } = row.dimensions

        let metric = null
        if( grouping === 'UNSEGMENTED' ){
          metric = unsegmentedMetrics.UNSEGMENTED
        }
        if( grouping === 'MARKETING_FUNNEL' ){
          metric = funnelMetrics[segment as keyof typeof funnelMetrics]
        }
        if( grouping === 'CAMPAIGN_OBJECTIVE' ){
          metric = videoViewMetrics['Video Views']
        }
        if( grouping === 'CHANNEL' ){
          metric = channelMetrics[segment as keyof typeof channelMetrics]
        }

        if( !metric ){
          continue
        }

        performanceRows.push({
          provider: provider as ReportProviderV2,
          segment,
          metric: metric.label,
          value: row.metrics[metric.key],
          org_unit_id: row.dimensions.org_unit_id,
          org_unit_name: row.dimensions.org_unit_name,
          period_start: dateRange.start,
          period_end: dateRange.end,
        })

      }
    }
  }

  return performanceRows
}


export const useCombinedSegmentation = ({
  orgUnit,
  dateRangePreset,
  currency,
  includePreviousPeriod = true,
  aggregateToSingleOrgUnit = false,
}: {
  orgUnit: PlatformUnit
  dateRangePreset: PerformanceTimeseriesPreset
  currency: string
  includePreviousPeriod?: boolean
  aggregateToSingleOrgUnit?: boolean
}): {
  combinedPerformanceQuery: QueryResultLike<DerivedPerformanceRow[]>
  dateRange: Record<'start' | 'end', Date | null>
  previousDateRange?: Record<'start' | 'end', Date | null>
} => {
  const orgUnitId = orgUnit.id

  const orgUnitHierarchy = useOrgUnitHierarchy({ orgUnitId })

  const { reportGroupings, availability } = useContext(OrgUnitDataWarehouseAvailabilityContext)

  const referenceDates = useContext(DashboardReferenceDatesContext)

  const configs = useMemo(() => {
    return getPerformanceReportAvailabilityByType({
      orgUnitId,
      orgUnitHierarchy,
      availabilityResponse: availability,
      groupings: reportGroupings,
    })
  }, [
    reportGroupings,
    availability,
    orgUnitId,
    orgUnitHierarchy,
  ])

  const maxDate = referenceDates.performanceReport.maxDate
  const [start, end] = (
    maxDate ?
      timeseriesPeriodCalculators[dateRangePreset](maxDate) :
      [null, null]
  )

  const previousDateRange = getPreviousDateRange({ start, end, dateRangePreset })

  const providers = uniq(
    flatten(
      Object.values(configs).map( c => c.providers )
    )
  )

  const startTs = start && start.getTime()
  const endTs = end && end.getTime()

  const combinedSegmentationQuery = useDataWarehouseQuery({
    grouping: 'COMBINED_SEGMENTATION',
    start,
    end,
    org_unit_ids: [orgUnitId],
    aggregate_to_single_org_unit: aggregateToSingleOrgUnit,
    providers,
    currency,
  })
  const previousCombinedSegmentationQuery = useDataWarehouseQuery({
    grouping: 'COMBINED_SEGMENTATION',
    ...previousDateRange,
    org_unit_ids: [orgUnitId],
    aggregate_to_single_org_unit: aggregateToSingleOrgUnit,
    providers,
    currency,
    enabled: includePreviousPeriod,
  })

  const validQuerySignals = [combinedSegmentationQuery]
  if( includePreviousPeriod ){
    validQuerySignals.push(previousCombinedSegmentationQuery)
  }
  
  const isLoading = some(validQuerySignals, q => q.isLoading)
  const isError = some(validQuerySignals, q => q.isError)
  const isRefetching = some(validQuerySignals, q => q.isRefetching)

  const data = useMemo(() => {
    const start = startTs && new Date(startTs)
    const end = endTs && new Date(endTs)
    if( start && end ){
      const previousDateRange = getPreviousDateRange({ start, end, dateRangePreset })
      return deriveCombinedPerformanceRows({
        data: combinedSegmentationQuery.data,
        dateRange: { start, end },
        previousData: previousCombinedSegmentationQuery.data,
        previousDateRange,
        includesPreviousPeriod: includePreviousPeriod,
      })
    }
    return []
  }, [
    combinedSegmentationQuery.data,
    previousCombinedSegmentationQuery.data,
    startTs,
    endTs,
    dateRangePreset,
    includePreviousPeriod,
  ])

  return {
    combinedPerformanceQuery: {
      isError,
      isLoading,
      isRefetching,
      data,
    },
    dateRange: { start, end },
    previousDateRange,
  }
}


export type OrgUnitDerivedPerformanceRowMapping = Partial<
  Record<
    ReportProviderV2,
    Record<
      string,
      Record<
        string, DerivedPerformanceRow[]
      >
    >
  >
>

export const getDerivedPerformanceByOrgUnit = (
  rows: DerivedPerformanceRow[],
): OrgUnitDerivedPerformanceRowMapping => {
  const mapping: OrgUnitDerivedPerformanceRowMapping = {}
  for( const row of rows ){
    const { provider, org_unit_id } = row
    mapping[provider] = mapping[provider] || {}
    const segment = row.segment || 'UNSEGMENTED'
    if( !mapping[provider][segment] ){
      mapping[provider][segment] = {}
    }
    if( !mapping[provider][segment][org_unit_id] ){
      mapping[provider][segment][org_unit_id] = [row]
    }else{
      mapping[provider][segment][org_unit_id].push(row)
      mapping[provider][segment][org_unit_id] = sortBy(
        mapping[provider][segment][org_unit_id],
        r => r.period_start.getTime()
      )
    }
  }
  return mapping
}

export type AdvertiserChangePerformanceRow = Omit<DerivedPerformanceRow, 'org_unit_id'> & {
  change: number | null
}

export type AdvertiserDerivedPerformanceRowMapping = Partial<
  Record<
    ReportProviderV2,
    Record<
      string, DerivedPerformanceRow[]
    >
  >
> 

export const getAdvertiserDerivedPerformanceMapping = (
  rows: DerivedPerformanceRow[]
): AdvertiserDerivedPerformanceRowMapping => {
  const mapping: AdvertiserDerivedPerformanceRowMapping = {}
  for( const row of rows ){
    const { provider } = row
    mapping[provider] = mapping[provider] || {}
    const segment = row.segment || 'UNSEGMENTED'
    if( !mapping[provider][segment] ){
      mapping[provider][segment] = [row]
    }else{
      mapping[provider][segment].push(row)
      mapping[provider][segment] = sortBy(
        mapping[provider][segment],
        r => r.period_start.getTime()
      )
    }
  }
  return mapping
}



interface SortablePerformanceRow {
  provider: ReportProviderV2
  segment: string | null
}

export const sortPerformaceDetailRows = <T extends SortablePerformanceRow>(
  rows: T[]
): T[] => (
    sortBy(
      rows,
      r => reportProvidersV2.indexOf(r.provider),
      r => r.segment,
    )
  )
